import React, { useContext } from "react";
import styled, { css, useTheme } from "styled-components";
import { ArrowLeft } from "lucide-react";
import Form from "antd/es/form";
import { useHistory } from "react-router-dom";
import IconWithFallback from "Global/IconWithFallback";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { BOTTOM_SUMMARY_CLASS, CREATE_ACCOUNT_PATH } from "common/constants";
import { EXCHANGES, ICON_TYPES } from "@shared/common/constants";
import {
  AddClientResponse,
  ClientData,
  LanguageNameSpaces,
} from "@shared/types";
import useConnectExchange from "@shared/features/portfolio/createAccount/useConnectExchange";
import { AccountFormContext } from "@shared/features/common/AccountForm.context";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useBottomNavigator } from "utils/hooks/misc";
import ClientForm from "features/configuration/accounts/CreateAccount/ClientForm";
import ChangeAccountTypeMessage from "features/configuration/accounts/CreateAccount/ChangeAccountTypeMessage";
import { stepTitle } from "features/onboarding/common/onboardingUi";
import {
  focusRing,
  LogoTile,
} from "features/portfolio/CreateAccount/NewLook/styles";

interface NewLookConnectExchangeProps {
  primaryExchange?: string;
  account?: ClientData;
  getErrorNotification?: (description: string, title?: string) => void;
  isOnboarding?: boolean;
  onSubmitSuccess?: () => void;
  showAccountTypeSwitch?: boolean;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
}

const PAGE_GUTTER = 16;

const NewLookConnectExchange = ({
  primaryExchange,
  account,
  getErrorNotification,
  isOnboarding,
  onSubmitSuccess,
  getSuccessMessage,
  showAccountTypeSwitch = true,
}: NewLookConnectExchangeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const history = useHistory();
  const [form] = Form.useForm();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isDemoWorkspace } = useContext(AccountFormContext)!;
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const { showTabs, closeDrawer, onBackToSelectExchange } = useConnectExchange({
    primaryExchange,
    account,
  });

  const isDrawerPhone = isBottomNavigator && !isOnboarding;
  const framePadding = isDrawerPhone
    ? { padding: `0 ${PAGE_GUTTER}px` }
    : undefined;
  const showChangeType =
    showAccountTypeSwitch && !account && !isExternalConnectAccount;
  const changeType = showChangeType && <ChangeAccountTypeMessage />;
  const title = isDemoWorkspace
    ? t("create_account.new_look.demo_connect_title", {
        exchange: primaryExchange,
      })
    : t("create_account.new_look.connect_title", { exchange: primaryExchange });

  const onBackClick = () => {
    history.push(CREATE_ACCOUNT_PATH);
    onBackToSelectExchange?.();
  };

  const getLogo = (size: number) =>
    primaryExchange && (
      <LogoTile $size={size}>
        <IconWithFallback
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={primaryExchange}
          pxIconSize={Math.round(size * 0.82)}
          iconStyle={{
            width: Math.round(size * 0.82),
            height: Math.round(size * 0.82),
            filter:
              primaryExchange === EXCHANGES.OKX && theme.isDarkMode
                ? "invert(1)"
                : undefined,
          }}
        />
      </LogoTile>
    );

  const getHeader = () => {
    if (isOnboarding) {
      return (
        <PageHeader>
          {getLogo(48)}
          <PageTitle>{title}</PageTitle>
        </PageHeader>
      );
    }
    if (account) return null;
    return (
      <DrawerHeader style={framePadding}>
        <BackButton
          type="button"
          onClick={onBackClick}
          aria-label={t("create_account.new_look.back_to_exchanges")}
        >
          <ArrowLeft size={18} strokeWidth={NEW_LOOK_ICON_STROKE} aria-hidden />
        </BackButton>
        {getLogo(32)}
        <DrawerTitle>{title}</DrawerTitle>
      </DrawerHeader>
    );
  };

  const clientForm = (
    <ClientForm
      form={form}
      getErrorNotification={getErrorNotification}
      isOnboarding={isOnboarding}
      onSubmitSuccess={onSubmitSuccess}
      primaryExchange={primaryExchange}
      account={account}
      isQuickConnect={showTabs}
      closeDrawer={closeDrawer}
      getSuccessMessage={getSuccessMessage}
      footer={isBottomNavigator ? undefined : changeType}
    />
  );

  if (isDrawerPhone) {
    return (
      <>
        {getHeader()}
        <PhoneScroll style={framePadding}>{clientForm}</PhoneScroll>
        {changeType && (
          <div style={framePadding} className={BOTTOM_SUMMARY_CLASS}>
            {/* One line at the bottom, so the form keeps the screen. */}
            <ChangeAccountTypeMessage
              inline={true}
              style={{ paddingTop: 10, paddingBottom: 10 }}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {getHeader()}
      {clientForm}
    </>
  );
};

export default NewLookConnectExchange;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;
const BackButton = styled.button`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 32px;
  height: 32px;
  margin-left: -6px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: ${(p) => p.theme.colors.secondaryText};
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
  ${focusRing};
`;
const DrawerTitle = styled.h2`
  margin: 0;
  min-width: 0;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 18px;
  line-height: 26px;
  font-weight: 520;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
  text-align: center;
  @media (max-width: 559px) {
    flex-direction: row;
    margin-bottom: 20px;
    text-align: left;
  }
`;
const PageTitle = styled.h1`
  margin: 0;
  ${stepTitle};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      @media (max-width: 559px) {
        font-size: 22px;
        line-height: 30px;
      }
    `}
`;
const PhoneScroll = styled.div`
  flex: 1;
  overflow: auto;
`;
